
import React, { useState } from 'react';
import Header from '../../components/feature/Header';
import Footer from '../../components/feature/Footer';

const BlogPage: React.FC = () => {
  const [selectedCategory, setSelectedCategory] = useState('All');
  const [searchTerm, setSearchTerm] = useState('');

  const categories = ['All', 'AI Technology', 'Real Estate Tips', 'Market Insights', 'Success Stories', 'Industry News'];

  const blogPosts = [
    {
      id: 1,
      title: 'How AI is Revolutionizing Real Estate Lead Generation in 2024',
      excerpt: 'Discover the latest AI technologies that are transforming how real estate agents generate and qualify leads, resulting in 300% higher conversion rates.',
      category: 'AI Technology',
      author: 'Sarah Johnson',
      date: '2024-01-15',
      readTime: '8 min read',
      image: 'https://public.readdy.ai/ai/img_res/01350cd760cfe043eaec9db0c8df60a2.jpg',
      featured: true
    },
    {
      id: 2,
      title: 'The Complete Guide to AI-Powered Property Staging',
      excerpt: 'Learn how virtual staging with AI can help you sell properties 40% faster while reducing staging costs by up to 90%.',
      category: 'AI Technology',
      author: 'Michael Chen',
      date: '2024-01-12',
      readTime: '12 min read',
      image: 'https://readdy.ai/api/search-image?query=beautiful%20modern%20living%20room%20with%20AI-generated%20furniture%20and%20decor%2C%20virtual%20staging%20concept%2C%20elegant%20interior%20design%20with%20natural%20lighting%2C%20professional%20real%20estate%20photography%20style&width=400&height=250&seq=blog2&orientation=landscape',
      featured: true
    },
    {
      id: 3,
      title: 'Market Analytics: Predicting Property Values with Machine Learning',
      excerpt: 'Explore how advanced algorithms analyze market trends, neighborhood data, and economic indicators to predict property values with 95% accuracy.',
      category: 'Market Insights',
      author: 'Dr. Emily Rodriguez',
      date: '2024-01-10',
      readTime: '10 min read',
      image: 'https://readdy.ai/api/search-image?query=data%20analytics%20dashboard%20showing%20real%20estate%20market%20trends%20and%20property%20value%20predictions%2C%20charts%20and%20graphs%20on%20computer%20screens%2C%20professional%20business%20analytics%20environment&width=400&height=250&seq=blog3&orientation=landscape',
      featured: false
    },
    {
      id: 4,
      title: 'Success Story: How Agent Lisa Doubled Her Sales with AI Automation',
      excerpt: 'Real estate agent Lisa Martinez shares how implementing AI automation tools helped her increase sales by 120% in just 6 months.',
      category: 'Success Stories',
      author: 'Lisa Martinez',
      date: '2024-01-08',
      readTime: '6 min read',
      image: 'https://readdy.ai/api/search-image?query=successful%20female%20real%20estate%20agent%20in%20professional%20attire%20standing%20in%20front%20of%20sold%20house%20signs%2C%20confident%20and%20happy%20expression%2C%20suburban%20neighborhood%20background&width=400&height=250&seq=blog4&orientation=landscape',
      featured: false
    },
    {
      id: 5,
      title: 'Building Stronger Client Relationships with AI Chatbots',
      excerpt: 'Discover how 24/7 AI chatbots can improve client communication, answer questions instantly, and schedule appointments automatically.',
      category: 'AI Technology',
      author: 'David Park',
      date: '2024-01-05',
      readTime: '7 min read',
      image: 'https://readdy.ai/api/search-image?query=modern%20smartphone%20showing%20AI%20chatbot%20interface%20for%20real%20estate%2C%20clean%20UI%20design%20with%20blue%20accents%2C%20professional%20communication%20technology%20concept&width=400&height=250&seq=blog5&orientation=landscape',
      featured: false
    },
    {
      id: 6,
      title: '2024 Real Estate Market Trends: What Agents Need to Know',
      excerpt: 'Stay ahead of the competition with insights into emerging market trends, buyer behavior changes, and technology adoption in real estate.',
      category: 'Market Insights',
      author: 'Jennifer Walsh',
      date: '2024-01-03',
      readTime: '9 min read',
      image: 'https://readdy.ai/api/search-image?query=real%20estate%20market%20trends%20visualization%20with%20rising%20graphs%20and%20charts%2C%20modern%20office%20setting%20with%20large%20displays%20showing%20market%20data%2C%20professional%20business%20environment&width=400&height=250&seq=blog6&orientation=landscape',
      featured: false
    },
    {
      id: 7,
      title: 'Maximizing ROI with Automated Social Media Marketing',
      excerpt: 'Learn how AI-powered social media tools can create engaging content, schedule posts, and generate leads while you focus on closing deals.',
      category: 'AI Technology',
      author: 'Alex Thompson',
      date: '2024-01-01',
      readTime: '8 min read',
      image: 'https://readdy.ai/api/search-image?query=social%20media%20marketing%20dashboard%20showing%20real%20estate%20content%20automation%2C%20multiple%20platform%20interfaces%2C%20engagement%20analytics%20and%20scheduling%20tools&width=400&height=250&seq=blog7&orientation=landscape',
      featured: false
    },
    {
      id: 8,
      title: 'The Future of Real Estate: AI Trends to Watch in 2024',
      excerpt: 'Explore upcoming AI innovations that will reshape the real estate industry, from virtual reality tours to predictive pricing models.',
      category: 'Industry News',
      author: 'Dr. Robert Kim',
      date: '2023-12-28',
      readTime: '11 min read',
      image: 'https://readdy.ai/api/search-image?query=futuristic%20real%20estate%20technology%20concept%20with%20virtual%20reality%20headsets%2C%20holographic%20property%20displays%2C%20and%20AI%20interfaces%2C%20modern%20tech%20environment&width=400&height=250&seq=blog8&orientation=landscape',
      featured: false
    }
  ];

  const filteredPosts = blogPosts.filter(post => {
    const matchesCategory = selectedCategory === 'All' || post.category === selectedCategory;
    const matchesSearch = post.title.toLowerCase().includes(searchTerm.toLowerCase()) ||
                         post.excerpt.toLowerCase().includes(searchTerm.toLowerCase());
    return matchesCategory && matchesSearch;
  });

  const featuredPosts = blogPosts.filter(post => post.featured);

  return (
    <div className="min-h-screen bg-white">
      <Header />
      
      {/* Hero Section */}
      <section className="bg-gradient-to-r from-blue-600 to-blue-800 text-white py-20">
        <div className="container mx-auto px-6">
          <div className="max-w-4xl mx-auto text-center">
            <h1 className="text-5xl font-bold mb-6">AI Real Estate Insights</h1>
            <p className="text-xl mb-8 opacity-90">
              Stay ahead with the latest trends, tips, and success stories in AI-powered real estate
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <div className="relative">
                <input
                  type="text"
                  placeholder="Search articles..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  className="w-full sm:w-80 px-4 py-3 pl-12 rounded-lg text-gray-900 focus:outline-none focus:ring-2 focus:ring-blue-300"
                />
                <i className="ri-search-line absolute left-4 top-1/2 transform -translate-y-1/2 text-gray-500"></i>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Featured Posts */}
      <section className="py-16 bg-gray-50">
        <div className="container mx-auto px-6">
          <h2 className="text-3xl font-bold text-center mb-12 text-gray-900">Featured Articles</h2>
          <div className="grid md:grid-cols-2 gap-8 max-w-6xl mx-auto">
            {featuredPosts.map((post) => (
              <article key={post.id} className="bg-white rounded-xl shadow-lg overflow-hidden hover:shadow-xl transition-shadow duration-300">
                <div className="relative">
                  <img 
                    src={post.image} 
                    alt={post.title}
                    className="w-full h-64 object-cover object-top"
                  />
                  <div className="absolute top-4 left-4">
                    <span className="bg-blue-600 text-white px-3 py-1 rounded-full text-sm font-medium">
                      Featured
                    </span>
                  </div>
                </div>
                <div className="p-6">
                  <div className="flex items-center gap-4 text-sm text-gray-600 mb-3">
                    <span className="bg-blue-100 text-blue-800 px-2 py-1 rounded-full text-xs font-medium">
                      {post.category}
                    </span>
                    <span>{post.readTime}</span>
                    <span>{new Date(post.date).toLocaleDateString()}</span>
                  </div>
                  <h3 className="text-xl font-bold mb-3 text-gray-900 hover:text-blue-600 cursor-pointer transition-colors">
                    {post.title}
                  </h3>
                  <p className="text-gray-600 mb-4 line-clamp-3">{post.excerpt}</p>
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-3">
                      <div className="w-8 h-8 bg-blue-600 rounded-full flex items-center justify-center">
                        <span className="text-white text-sm font-medium">
                          {post.author.split(' ').map(n => n[0]).join('')}
                        </span>
                      </div>
                      <span className="text-sm text-gray-700 font-medium">{post.author}</span>
                    </div>
                    <button className="text-blue-600 hover:text-blue-800 font-medium text-sm flex items-center gap-1 cursor-pointer">
                      Read More <i className="ri-arrow-right-line"></i>
                    </button>
                  </div>
                </div>
              </article>
            ))}
          </div>
        </div>
      </section>

      {/* Category Filter */}
      <section className="py-8 bg-white border-b">
        <div className="container mx-auto px-6">
          <div className="flex flex-wrap gap-3 justify-center">
            {categories.map((category) => (
              <button
                key={category}
                onClick={() => setSelectedCategory(category)}
                className={`px-6 py-2 rounded-full font-medium transition-all whitespace-nowrap ${
                  selectedCategory === category
                    ? 'bg-blue-600 text-white'
                    : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                }`}
              >
                {category}
              </button>
            ))}
          </div>
        </div>
      </section>

      {/* All Posts */}
      <section className="py-16">
        <div className="container mx-auto px-6">
          <div className="max-w-6xl mx-auto">
            <div className="flex items-center justify-between mb-8">
              <h2 className="text-3xl font-bold text-gray-900">
                {selectedCategory === 'All' ? 'All Articles' : selectedCategory}
              </h2>
              <span className="text-gray-600">
                {filteredPosts.length} article{filteredPosts.length !== 1 ? 's' : ''}
              </span>
            </div>
            
            <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
              {filteredPosts.map((post) => (
                <article key={post.id} className="bg-white rounded-xl shadow-lg overflow-hidden hover:shadow-xl transition-shadow duration-300">
                  <div className="relative">
                    <img 
                      src={post.image} 
                      alt={post.title}
                      className="w-full h-48 object-cover object-top"
                    />
                    {post.featured && (
                      <div className="absolute top-3 left-3">
                        <span className="bg-blue-600 text-white px-2 py-1 rounded-full text-xs font-medium">
                          Featured
                        </span>
                      </div>
                    )}
                  </div>
                  <div className="p-6">
                    <div className="flex items-center gap-3 text-sm text-gray-600 mb-3">
                      <span className="bg-blue-100 text-blue-800 px-2 py-1 rounded-full text-xs font-medium">
                        {post.category}
                      </span>
                      <span>{post.readTime}</span>
                    </div>
                    <h3 className="text-lg font-bold mb-3 text-gray-900 hover:text-blue-600 cursor-pointer transition-colors line-clamp-2">
                      {post.title}
                    </h3>
                    <p className="text-gray-600 mb-4 text-sm line-clamp-3">{post.excerpt}</p>
                    <div className="flex items-center justify-between">
                      <div className="flex items-center gap-2">
                        <div className="w-6 h-6 bg-blue-600 rounded-full flex items-center justify-center">
                          <span className="text-white text-xs font-medium">
                            {post.author.split(' ').map(n => n[0]).join('')}
                          </span>
                        </div>
                        <span className="text-xs text-gray-700">{post.author}</span>
                      </div>
                      <span className="text-xs text-gray-500">{new Date(post.date).toLocaleDateString()}</span>
                    </div>
                  </div>
                </article>
              ))}
            </div>

            {filteredPosts.length === 0 && (
              <div className="text-center py-12">
                <i className="ri-search-line text-6xl text-gray-300 mb-4"></i>
                <h3 className="text-xl font-semibold text-gray-600 mb-2">No articles found</h3>
                <p className="text-gray-500">Try adjusting your search or filter criteria</p>
              </div>
            )}
          </div>
        </div>
      </section>

      {/* Newsletter Subscription */}
      <section className="py-16 bg-blue-600">
        <div className="container mx-auto px-6">
          <div className="max-w-4xl mx-auto text-center">
            <h2 className="text-3xl font-bold text-white mb-4">Stay Updated</h2>
            <p className="text-xl text-blue-100 mb-8">
              Get the latest AI real estate insights delivered to your inbox weekly
            </p>
            <form className="flex flex-col sm:flex-row gap-4 justify-center max-w-md mx-auto">
              <input
                type="email"
                placeholder="Enter your email"
                className="flex-1 px-4 py-3 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-300"
                required
              />
              <button
                type="submit"
                className="bg-white text-blue-600 px-8 py-3 rounded-lg font-semibold hover:bg-gray-100 transition-colors whitespace-nowrap"
              >
                Subscribe
              </button>
            </form>
            <p className="text-blue-200 text-sm mt-4">
              No spam. Unsubscribe at any time.
            </p>
          </div>
        </div>
      </section>

      <Footer />
    </div>
  );
};

export default BlogPage;
